/**
 * ListingGrid - Grid layout for displaying property listings
 * 
 * Provides a responsive grid of ListingCard components with support for:
 * - Multiple layout variants (grid, list, masonry)
 * - Empty state handling
 * - Loading skeleton state
 */

'use client';

import type { ListingCardData } from './types';
import { ListingCard } from './ListingCard';
import { WishlistStatusProvider } from '@/hooks/useWishlist';

export interface ListingGridProps {
  listings: ListingCardData[];
  basePath: string;
  variant?: 'grid' | 'list';
  columns?: 1 | 2 | 3 | 4;
  showAgent?: boolean;
  /** Whether to show the agent avatar with name popover on cards - defaults to true */
  showAgentAvatar?: boolean;
  showStatus?: boolean;
  /** Whether to show the wishlist (heart) button on cards - defaults to true */
  showWishlistButton?: boolean;
  /** Entity type to save to wishlist - PROPERTY saves the property, LISTING saves the listing */
  wishlistEntityType?: 'PROPERTY' | 'LISTING';
  locale?: string;
  emptyMessage?: string;
  emptyDescription?: string;
  className?: string;
}

export function ListingGrid({
  listings,
  basePath,
  variant = 'grid',
  columns = 3,
  showAgent = false,
  showAgentAvatar = true,
  showStatus = true,
  showWishlistButton = true,
  wishlistEntityType = 'PROPERTY',
  locale = 'en',
  emptyMessage = 'No listings found',
  emptyDescription = 'Check back later for new listings.',
  className = '',
}: ListingGridProps) {
  // Empty state
  if (listings.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
        <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-1">{emptyMessage}</h3>
        <p className="text-gray-500 max-w-sm">{emptyDescription}</p>
      </div>
    );
  }

  // Grid layout
  const gridClasses = variant === 'list' 
    ? 'flex flex-col gap-4'
    : `grid gap-6 ${
        columns === 1 ? 'grid-cols-1' :
        columns === 2 ? 'grid-cols-1 md:grid-cols-2' :
        columns === 4 ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4' :
        'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
      }`;

  const grid = (
    <div className={`${gridClasses} ${className}`}>
      {listings.map((listing) => (
        <ListingCard
          key={listing.id}
          listing={listing}
          href={`${basePath}/properties/${listing.property.id}`}
          variant={variant === 'list' ? 'compact' : 'default'}
          showAgent={showAgent}
          showAgentAvatar={showAgentAvatar}
          showStatus={showStatus}
          showWishlistButton={showWishlistButton}
          wishlistEntityType={wishlistEntityType}
          locale={locale}
        />
      ))}
    </div>
  );

  // Wrap with WishlistStatusProvider to batch wishlist checks into a single API call
  // instead of each card making its own request (N+1 problem)
  if (showWishlistButton) {
    return <WishlistStatusProvider>{grid}</WishlistStatusProvider>;
  }

  return grid;
}

/**
 * Loading skeleton for ListingGrid
 */
export function ListingGridSkeleton({
  count = 6,
  columns = 3,
  className = '',
}: {
  count?: number;
  columns?: 1 | 2 | 3 | 4;
  className?: string;
}) {
  const gridClasses = `grid gap-6 ${
    columns === 1 ? 'grid-cols-1' :
    columns === 2 ? 'grid-cols-1 md:grid-cols-2' :
    columns === 4 ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4' :
    'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
  }`;

  return (
    <div className={`${gridClasses} ${className}`}>
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="bg-white rounded-lg overflow-hidden shadow-md animate-pulse">
          {/* Image skeleton */}
          <div className="aspect-[4/3] bg-gray-200" />
          
          {/* Content skeleton */}
          <div className="p-4 space-y-3">
            {/* Location skeleton */}
            <div className="h-3 bg-gray-200 rounded w-1/2" />
            
            {/* Title skeleton */}
            <div className="h-5 bg-gray-200 rounded w-3/4" />
            
            {/* Price skeleton */}
            <div className="h-6 bg-gray-200 rounded w-1/3" />
            
            {/* Details skeleton */}
            <div className="flex gap-3">
              <div className="h-4 bg-gray-200 rounded w-12" />
              <div className="h-4 bg-gray-200 rounded w-12" />
              <div className="h-4 bg-gray-200 rounded w-16" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default ListingGrid;
